import {
  Box,
  Grid,
  InputAdornment,
  Link,
  List,
  makeStyles,
  TextField,
  Theme,
  useTheme,
} from '@material-ui/core';
import { Search } from '@material-ui/icons';
import _ from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { TagFilterInputGq } from '../../../../../../graphql/projects/models/TagFilterInputGq';
import { TagGq } from '../../../../../../graphql/projects/models/TagGq';
import { TagGroupGq } from '../../../../../../graphql/projects/models/TagGroupGq';
import VoxelTooltipShort from '../../../../../../components/voxel/Tooltip/VoxelTooltipShort';
import HeadCell from '../../../../../../components/muiLegacy/Tables/Shared/HeadCell';
import HeadCellSelectOption from '../../../../../../components/muiLegacy/Tables/Shared/HeadCellTagSelection';
import { ConflictTableState } from './logic/useConflictTableState';

export interface ConflictsTagFilterProps {
  tableState: ConflictTableState;
  tagGroup: TagGroupGq;
  tags: TagGq[];
}

const useClasses = makeStyles((theme: Theme) => ({
  tagList: {
    height: 140,
    overflowY: 'scroll',
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.grey[400],
      borderRadius: 7,
    },
  },
  linkButton: {
    marginRight: '3px',
    marginLeft: '3px',
    '&[disabled]': {
      color: 'grey',
      cursor: 'default',
      textDecoration: 'none',
    },
  },
  divider: {
    marginTop: '2px',
    marginBottom: '2px',
  },
  xsInputField: {
    paddingTop: '8px',
    paddingBottom: '8px',
  },
  greyShadowInputField: {
    outline: 'none',
    border: 'none',
    boxShadow: 'inset 0px 0px 50px -3px rgba(0,0,0,0.04)',
    borderRadius: '3px',
  },
  greyShadowInputFieldFocused: {
    boxShadow: 'inset 0px 0px 50px -3px rgba(0,0,0,0.02)',
  },
}));

const ConflictsTagFilter: React.FC<ConflictsTagFilterProps> = ({ tableState, tagGroup, tags }) => {
  // Keyword table state
  const { filterState, setFilterState } = tableState;

  const tagsMap = useMemo(() => {
    const result: Record<number, TagGq> = {};
    tags.forEach((t) => {
      result[t.id] = t;
    });
    return result;
  }, [tags]);

  // Filter
  const filterValue = useMemo(
    () => filterState.conflictTagFilters.filter((tf) => tf.tagGroupId === tagGroup.id),
    [filterState.conflictTagFilters, tagGroup.id]
  );

  const filterOptions: TagFilterInputGq[] = useMemo(
    () =>
      _.chain([...tags?.map((tag) => ({ tagId: tag.id, tagGroupId: tag.tagGroupId }))])
        .orderBy([(t) => t.tagId !== undefined, (t) => t.tagGroupId, (t) => tagsMap[t.tagId]?.name])
        .value(),
    [tags, tagGroup]
  );

  const handleFilterChange = useCallback(
    (value: TagFilterInputGq, checked: boolean) =>
      setFilterState((prev) => {
        const conflictTagFilters = [
          ...prev.conflictTagFilters.filter(
            (tf) => !(tf.tagGroupId === value.tagGroupId && tf.tagId === value.tagId)
          ),
        ];
        checked && conflictTagFilters.push(value);
        return {
          ...prev,
          conflictTagFilters,
        };
      }),
    [tagGroup.id]
  );

  const selectAllTags = useCallback(() => {
    setFilterState((prev) => {
      const conflictTagFilters = prev.conflictTagFilters.filter(
        (tF) => tF.tagGroupId != tagGroup.id
      );
      displayedTags.forEach((tag) =>
        conflictTagFilters.push({ tagId: tag.tagId, tagGroupId: tag.tagGroupId })
      );
      return { ...prev, conflictTagFilters };
    });
  }, [tags, tagGroup.id]);

  const resetTagFilter = useCallback(() => {
    setFilterState((prev) => {
      const conflictTagFilters = prev.conflictTagFilters.filter(
        (tF) => tF.tagGroupId != tagGroup.id
      );
      return { ...prev, conflictTagFilters };
    });
  }, [tags, tagGroup.id]);

  // Tag search
  const [tagSearchText, setTagSearchText] = useState('');

  const displayedTags = useMemo(() => {
    if (!tagSearchText.length) {
      return filterOptions;
    }
    const words = tagSearchText.split(' ')?.map((word) => word.toLowerCase());
    return filterOptions.filter((option) =>
      words.every((word) => tagsMap[option.tagId]?.name?.toLowerCase().includes(word))
    );
  }, [tagSearchText, filterOptions]);

  // styling
  const theme = useTheme();
  const classes = useClasses(theme);

  return (
    <div className="p-2 w-64 flex flex-col gap-2 ">
      <div className="flex flex-row">
        <Link
          component="button"
          variant="caption"
          underline="always"
          onClick={selectAllTags}
          className={classes.linkButton}
          disabled={filterValue.length === displayedTags.length}
        >
          Select all
        </Link>
        <VoxelTooltipShort message="clear selection" disabled={!filterValue.length}>
          <Link
            component="button"
            variant="caption"
            underline="always"
            onClick={resetTagFilter}
            className={classes.linkButton}
            disabled={!filterValue.length}
          >
            Clear
          </Link>
        </VoxelTooltipShort>
      </div>
      <TextField
        fullWidth
        autoFocus
        size={'small'}
        variant={'outlined'}
        onChange={(event) => setTagSearchText(event.target.value)}
        value={tagSearchText}
        InputProps={{
          endAdornment: (
            <InputAdornment position={'end'}>
              <Search fontSize={'small'} />
            </InputAdornment>
          ),
          classes: {
            inputMarginDense: classes.xsInputField,
            notchedOutline: classes.greyShadowInputField,
            focused: classes.greyShadowInputFieldFocused,
          },
        }}
      />
      <div>
        <List className={classes.tagList} dense disablePadding>
          {displayedTags?.map((displayedTag) => (
            <HeadCellSelectOption
              key={displayedTag.tagId ?? -1}
              option={displayedTag}
              optionDisplayName={tagsMap[displayedTag.tagId]?.name ?? ''}
              handleFilterChange={handleFilterChange}
              filter={filterValue}
            />
          ))}
        </List>
      </div>
    </div>
  );
};

export default ConflictsTagFilter;
